import { CcfBox, CcfTabs, CcfTooltip } from '@nice-ccf/ui-controls';
import { IconButton } from '@mui/material';
import styles from './simulator-v2-create-new-journey.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import SimulatorV2HelpPanel from '../simulator-v2-help/simulator-v2-help-panel';
import SimulatorV2JourneyDetailsTab from './simulator-v2-journey-details-tab/simulator-v2-journey-details-tab';
import SimulatorV2CustomerDetailsTab from './simulator-v2-customer-details-tab/simulator-v2-customer-details-tab';
import SimulatorV2OverviewTab from './simulator-v2-overview-tab/simulator-v2-overview-tab';
import SimulatorV2InteractionHistoryTab from './simulator-v2-interaction-history-tab/simulator-v2-interaction-history-tab';
import SimulatorV2ContactHistoryTab from './simulator-v2-contact-history-tab/simulator-v2-contact-history-tab';
import SimulatorV2ExternalDataTab from './simulator-v2-external-data-tab/simulator-v2-external-data-tab';
import SimulatorV2CallTranscriptTab from './simulator-v2-call-transcript-tab/simulator-v2-call-transcript-tab';
import SimulatorV2CopilotTab from './simulator-v2-copilot-tab/simulator-v2-copilot-tab';
import SimulatorV2ProcessAutomationTab from './simulator-v2-process-automation-tab/simulator-v2-process-automation-tab';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedChannel,
  getSelectedContact,
  getSelectedMedia,
  setSelectedContact,
} from '../+state/simulatorV2.slice';

const useStyles = makeStyles({
  root: {
    '& .MuiTabs-root': {
      '& .MuiButtonBase-root': {
        fontWeight: 700,
      },
      '& .Mui-selected': {
        color: '#1c639c',
      },
    },
  },
});

/* eslint-disable-next-line */
export interface SimulatorV2CreateNewJourneyProps {
  rowData?: any[];
  setIsErrorsResolve?: any;
  updatedContact?: any;
  setUpdatedContact?: any;
  isEditClick?: boolean;
  setIsCopilotValid?: (valid: boolean) => void;
  copilotManualDraft?: any;
  setCopilotManualDraft?: (draft: any) => void;
  copilotSnapshotByMedia?: Record<string, any>;
}

interface journeyTab {
  element: React.ReactElement;
  label: string;
  isActive: boolean;
  isEditClick?: boolean;
}

/**
 * used to Open the Create New Journey Layout
 * @param props - SimulatorV2CreateNewJourneyProps
 * @example ` <SimulatorV2CreateNewJourney />`
 */
export function SimulatorV2CreateNewJourney(
  props: SimulatorV2CreateNewJourneyProps
) {
  const {
    updatedContact,
    setUpdatedContact,
    isEditClick,
    setIsCopilotValid,
    copilotManualDraft,
    setCopilotManualDraft,
    copilotSnapshotByMedia,
  } = props;
  const selectedContact: any = useSelector(getSelectedContact);
  const [notesErrors, setNotesErrors] = useState<{ [key: string]: string }>({});
  const newMedia = useSelector(getSelectedMedia);
  const newChannel = useSelector(getSelectedChannel);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  useEffect(() => {
    dispatch(
      setSelectedContact({
        ...updatedContact,
      })
    );
  }, [updatedContact]);

  const isCopilotTabActive =
    newMedia === 'Voice' ||
    newMedia === 'VideoCall' ||
    (newMedia === 'Digital' && ['Email', 'Chat'].includes(newChannel));

  // When the Copilot tab isn't available for the current media/channel there is
  // nothing to validate, so keep the parent's Save unblocked.
  useEffect(() => {
    if (!isCopilotTabActive) setIsCopilotValid?.(true);
  }, [isCopilotTabActive]);

  // Discard the Build Manually draft when the media/channel changes: the draft
  // belongs to the previous media, and the Journey Details tab already resets
  // the Copilot content, so the Copilot tab should reload the new media's
  // default template instead of restoring the stale draft. Skips the initial
  // run so it doesn't wipe a draft while simply opening a journey for edit.
  const mediaChannelDraftInitRef = useRef(true);
  useEffect(() => {
    if (mediaChannelDraftInitRef.current) {
      mediaChannelDraftInitRef.current = false;
      return;
    }
    setCopilotManualDraft?.(undefined);
  }, [newMedia, newChannel]);

  const isProcessAutomationTabActive =
    newMedia === 'Digital' && newChannel === 'Chat';

  // Changing media/channel on a new journey replaces the contact with that
  // media's default contact, which has no Process Automation config. Carry the
  // configured workflows over so they survive the switch.
  const setUpdatedContactKeepingProcessAutomation = useCallback(
    (updater: any) =>
      setUpdatedContact((prev: any) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return prev?.processAutomation && next && !next.processAutomation
          ? { ...next, processAutomation: prev.processAutomation }
          : next;
      }),
    [setUpdatedContact]
  );

  const journeyTabs: journeyTab[] = [
    {
      element: (
        <SimulatorV2JourneyDetailsTab
          updatedContact={updatedContact}
          setUpdatedContact={setUpdatedContactKeepingProcessAutomation}
          isEditClick={isEditClick}
        />
      ),
      isActive: true,
      label: 'Journey Details',
    },
    {
      element: (
        <SimulatorV2CustomerDetailsTab
          updatedContact={updatedContact}
          setUpdatedContact={setUpdatedContact}
        />
      ),
      isActive: true,
      label: 'Customer Details',
    },
    {
      element: (
        <SimulatorV2OverviewTab
          updatedContact={updatedContact}
          setUpdatedContact={setUpdatedContact}
          notesErrors={notesErrors}
          setNotesErrors={setNotesErrors}
        />
      ),
      isActive: true,
      label: 'Overview',
    },
    {
      element: (
        <SimulatorV2InteractionHistoryTab
          updatedContact={updatedContact}
          setUpdatedContact={setUpdatedContact}
        />
      ),
      isActive: true,
      label: 'Interaction History',
    },
    {
      element: (
        <SimulatorV2ContactHistoryTab
          updatedContact={updatedContact}
          setUpdatedContact={setUpdatedContact}
        />
      ),
      isActive: true,
      label: 'Contact History',
    },
    {
      element: (
        <SimulatorV2ExternalDataTab
          updatedContact={updatedContact}
          setUpdatedContact={setUpdatedContact}
        />
      ),
      isActive: true,
      label: 'External Data',
    },
    // Show/Hide Transcript is a voice-only feature
    ...(newMedia === 'Voice'
      ? [
          {
            element: (
              <SimulatorV2CallTranscriptTab
                updatedContact={updatedContact}
                setUpdatedContact={setUpdatedContact}
              />
            ),
            isActive: true,
            label: 'Call Transcript',
          },
        ]
      : []),
    ...(isCopilotTabActive
      ? [
          {
            element: (
              <SimulatorV2CopilotTab
                updatedContact={updatedContact}
                setUpdatedContact={setUpdatedContact}
                setIsCopilotValid={setIsCopilotValid}
                copilotManualDraft={copilotManualDraft}
                setCopilotManualDraft={setCopilotManualDraft}
                copilotSnapshotByMedia={copilotSnapshotByMedia}
              />
            ),
            isActive: true,
            label: 'Copilot',
          },
        ]
      : []),
    // Process Automation is supported for Digital Chat journeys only for now
    ...(isProcessAutomationTabActive
      ? [
          {
            element: (
              <SimulatorV2ProcessAutomationTab
                updatedContact={updatedContact}
                setUpdatedContact={setUpdatedContact}
              />
            ),
            isActive: true,
            label: 'Process Automation',
          },
        ]
      : []),
  ];
  const activeTabs = journeyTabs.filter((tab) => tab.isActive);

  /**
   *
   * @param e - event
   * @example handleTabChange
   */
  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <CcfBox className={styles.createNewJourneyContainer}>
      <CcfBox className={styles.tabBarHeader}>
        <CcfTooltip title="Help" arrow>
          <IconButton aria-label="Help" onClick={() => setIsHelpOpen(true)}>
            <ContactSupportIcon />
          </IconButton>
        </CcfTooltip>
      </CcfBox>
      <SimulatorV2HelpPanel
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        activeSectionId={activeTabs[activeTab]?.label}
      />
      <CcfBox className={classes.root}>
        <CcfTabs
          currentTab={activeTab}
          variant={CcfTabs.Variant.FULL_WIDTH}
          setCurrentTab={handleTabChange}
          bottomBorder={true}
        >
          {activeTabs.map((tab, index) => (
            <CcfTabs.TabPanel key={index} label={tab.label}>
              <CcfBox className={styles.container}>{tab.element}</CcfBox>
            </CcfTabs.TabPanel>
          ))}
        </CcfTabs>
      </CcfBox>
    </CcfBox>
  );
}

export default SimulatorV2CreateNewJourney;
